import { useState, useEffect } from 'react';
import { useTitle } from '../../context/TitleContext';
import { Card, CardBody, CardHeader, CardTitle, Row, Col, Badge, Table, Spinner, Alert } from 'react-bootstrap';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import apiService from '../../services/api.service';
import '../../styles/products.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const StockReport = () => {
  const { setTitle } = useTitle();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    setTitle('Rapport de Stock');
    fetchReportData();
  }, [setTitle]);

  const fetchReportData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiService.get('/reports/stock');
      if (response.success) {
        setReportData(response.data);
      } else {
        setError('Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('Error fetching stock report:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-2">Chargement des données...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        <h4 className="alert-heading">Erreur!</h4>
        <p>{error}</p>
        <hr />
        <button className="btn btn-outline-danger" onClick={fetchReportData}>
          Réessayer
        </button>
      </div>
    );
  }

  if (!reportData) {
    return (
      <div className="alert alert-warning" role="alert">
        Aucune donnée disponible
      </div>
    );
  }

  const { categoryStockData, stockLevels, stockValueData, movementData, lowStockProducts, outOfStockProducts, topSellingProducts, summary } = reportData;

  // Stock by category bar chart
  const categoryBarData = {
    labels: categoryStockData.labels,
    datasets: [
      {
        label: 'Quantité en stock',
        data: categoryStockData.stock,
        backgroundColor: '#4ECDC4',
        borderColor: '#26A69A',
        borderWidth: 1,
      },
    ],
  };

  // Stock levels doughnut
  const stockLevelsChartData = {
    labels: stockLevels.labels,
    datasets: [
      {
        data: stockLevels.values,
        backgroundColor: stockLevels.colors,
        borderWidth: 2,
      },
    ],
  };

  // Stock value by category doughnut
  const stockValueChartData = {
    labels: stockValueData.labels.length > 0 ? stockValueData.labels : ['Aucune donnée'],
    datasets: [
      {
        data: stockValueData.values.length > 0 ? stockValueData.values : [1],
        backgroundColor: ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD', '#87CEEB', '#F4A460'],
        borderWidth: 2,
      },
    ],
  };

  // Monthly movement line chart
  const movementChartData = {
    labels: movementData.labels,
    datasets: [
      {
        label: 'Produits vendus',
        data: movementData.sold,
        borderColor: '#c5a05e',
        backgroundColor: 'rgba(197, 160, 94, 0.1)',
        tension: 0.4,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 20,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#4ECDC4',
        borderWidth: 1,
        cornerRadius: 8,
        displayColors: true,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11,
          },
        },
      },
    },
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return context.label + ': ' + context.parsed.toLocaleString() + ' (' + percentage + '%)';
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#4ECDC4',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
  };

  const valueChartOptions = {
    ...doughnutOptions,
    plugins: {
      ...doughnutOptions.plugins,
      tooltip: {
        callbacks: {
          label: function(context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return context.label + ': ' + context.parsed.toLocaleString() + ' DH (' + percentage + '%)';
          },
        },
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#4ECDC4',
        borderWidth: 1,
        cornerRadius: 8,
      },
    },
  };

  return (
    <div className="stock-report-page">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <Badge bg="secondary">Données en temps réel</Badge>
        </h5>
        <button className="btn btn-outline-primary btn-sm" onClick={fetchReportData}>
          <i className="solar:refresh-circle-bold-duotone me-1"></i>
          Actualiser
        </button>
      </div>

      <Row className="g-3 mb-4">
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:box-bold-duotone fs-2 text-primary me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalProducts.toLocaleString()}</h4>
              </div>
              <p className="text-muted mb-0">Total Produits</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:layers-bold-duotone fs-2 text-info me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalStock.toLocaleString()}</h4>
              </div>
              <p className="text-muted mb-0">Unités en Stock</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:dollar-bold-duotone fs-2 text-success me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalStockValue.toLocaleString()} DH</h4>
              </div>
              <p className="text-muted mb-0">Valeur du Stock</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={3} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <i className="solar:chart-2-bold-duotone fs-2 text-warning me-2"></i>
                <h4 className="mb-0" style={{ color: '#c5a05e' }}>{summary.totalSold.toLocaleString()}</h4>
              </div>
              <p className="text-muted mb-0">Produits Vendus</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mb-4">
        <Col xl={4} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <Badge bg="success" className="px-3 py-2 fs-6">{summary.inStock}</Badge>
              <p className="text-muted mb-0 mt-2">Produits en Stock</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={4} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <Badge bg="warning" className="px-3 py-2 fs-6">{summary.lowStock}</Badge>
              <p className="text-muted mb-0 mt-2">Stock Faible</p>
            </CardBody>
          </Card>
        </Col>
        <Col xl={4} lg={6} md={6}>
          <Card className="text-center h-100">
            <CardBody>
              <Badge bg="danger" className="px-3 py-2 fs-6">{summary.outOfStock}</Badge>
              <p className="text-muted mb-0 mt-2">Rupture de Stock</p>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3">
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Stock par Catégorie</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Bar data={categoryBarData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Niveaux de Stock</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Doughnut data={stockLevelsChartData} options={doughnutOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Valeur du Stock par Catégorie (DH)</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Doughnut data={stockValueChartData} options={valueChartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
        <Col xl={6}>
          <Card>
            <CardHeader>
              <CardTitle as="h6" className="mb-0">Ventes Mensuelles (Unités)</CardTitle>
            </CardHeader>
            <CardBody>
              <div style={{ height: '300px' }}>
                <Line data={movementChartData} options={chartOptions} />
              </div>
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={6}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle as="h6" className="mb-0">
                <i className="solar:danger-triangle-bold-duotone text-warning me-2"></i>
                Alertes Stock Faible
              </CardTitle>
              <Badge bg="warning">{lowStockProducts.length}</Badge>
            </CardHeader>
            <CardBody>
              {lowStockProducts.length > 0 ? (
                <Table striped hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Catégorie</th>
                      <th className="text-center">Stock</th>
                      <th className="text-center">Alerte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lowStockProducts.map((product) => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td><small className="text-muted">{product.category}</small></td>
                        <td className="text-center">
                          <Badge bg="warning">{product.stock}</Badge>
                        </td>
                        <td className="text-center">
                          <small className="text-muted">{product.stockAlert}</small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="success" className="mb-0">
                  <i className="solar:check-circle-bold-duotone me-2"></i>
                  Aucun produit en stock faible
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
        <Col xl={6}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle as="h6" className="mb-0">
                <i className="solar:close-circle-bold-duotone text-danger me-2"></i>
                Produits Épuisés
              </CardTitle>
              <Badge bg="danger">{outOfStockProducts.length}</Badge>
            </CardHeader>
            <CardBody>
              {outOfStockProducts.length > 0 ? (
                <Table striped hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>Produit</th>
                      <th>Catégorie</th>
                      <th className="text-center">Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {outOfStockProducts.map((product) => (
                      <tr key={product.id}>
                        <td>{product.name}</td>
                        <td><small className="text-muted">{product.category}</small></td>
                        <td className="text-center">
                          <Badge bg="danger">{product.stock}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="success" className="mb-0">
                  <i className="solar:check-circle-bold-duotone me-2"></i>
                  Aucun produit épuisé
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-2">
        <Col xl={12}>
          <Card>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle as="h6" className="mb-0">
                <i className="solar:star-bold-duotone text-warning me-2"></i>
                Top 10 Produits les Plus Vendus
              </CardTitle>
            </CardHeader>
            <CardBody>
              {topSellingProducts.length > 0 ? (
                <Table striped hover responsive size="sm">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Produit</th>
                      <th>Catégorie</th>
                      <th className="text-center">Vendus</th>
                      <th className="text-center">Stock Restant</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSellingProducts.map((product, index) => (
                      <tr key={product.id}>
                        <td>
                          <Badge bg={index < 3 ? 'warning' : 'secondary'}>{index + 1}</Badge>
                        </td>
                        <td>{product.name}</td>
                        <td><small className="text-muted">{product.category}</small></td>
                        <td className="text-center">
                          <Badge bg="success">{product.soldCount}</Badge>
                        </td>
                        <td className="text-center">
                          <Badge bg={product.stock > 10 ? 'primary' : product.stock > 0 ? 'warning' : 'danger'}>
                            {product.stock}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info" className="mb-0">
                  <i className="solar:info-circle-bold-duotone me-2"></i>
                  Aucune donnée de vente disponible
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default StockReport;